@file:OptIn(ExperimentalMaterial3Api::class)

package us.laantech.admin.menu.accounts

import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.text.input.TextFieldState
import androidx.compose.material3.BasicAlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextField
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.lifecycle.ViewModel
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import androidx.lifecycle.viewmodel.compose.viewModel
import androidx.lifecycle.viewmodel.initializer
import androidx.lifecycle.viewmodel.viewModelFactory
import io.ktor.client.call.body
import io.ktor.client.request.get
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType.Application.Json
import io.ktor.http.contentType
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import us.laantech.common.dto.UserGroups
import us.laantech.common.ui.DataTableView
import us.laantech.common.api.KtorClient
import us.laantech.routes.users.UserDTO

data class AccountsUiState(
    val users: List<UserDTO> = listOf<UserDTO>(),
    val groups: List<String> = listOf("INTERNAL", "BUSINESS", "EXTERNAL", "CONSUMER"),

    // Create Account Modal
    val showCreateAccountDialog: Boolean = false,
    val displayNameTextFieldState: TextFieldState = TextFieldState(),
    val usernameTextFieldState: TextFieldState = TextFieldState(),
    val passwordTextFieldState: TextFieldState = TextFieldState(),
    val emailTextFieldState: TextFieldState = TextFieldState(),
    val selectedGroup: String = "INTERNAL",

    )

@Composable
fun AccountsView(
    viewModel: AccountsViewModel = viewModel(
        factory = viewModelFactory {
            initializer { AccountsViewModel() }
        }
    )
) {
    val uiState by viewModel.uiState.collectAsStateWithLifecycle()

    LaunchedEffect(Unit) {
        viewModel.loadAccounts()
    }

    Column {
        Text("Accounts")
        Button({ viewModel.toggleCreateAccountDialog() }) {
            Text("Create Account")
        }

        DataTableView<UserDTO>(
            modifier = Modifier.fillMaxSize(),
            rows = uiState.users,
        ) {
            column("Name") { Text(it.displayName ?: "", modifier = Modifier.weight(1f)) }
            column("Username") { Text("${it.username}", modifier = Modifier.weight(1f)) }
            column("Email") { Text("${it.email}", modifier = Modifier.weight(1f)) }
            column("Group") { Text("${it.group}", modifier = Modifier.weight(1f)) }
            column("Enabled") { Text("${it.enabled}", modifier = Modifier.weight(1f)) }
        }

    }

    if (uiState.showCreateAccountDialog) {
        CreateAccountModal(viewModel)
    }
}


@Composable
fun CreateAccountModal(viewModel: AccountsViewModel) {

    val uiState by viewModel.uiState.collectAsStateWithLifecycle()
    var groupDropdownExpanded by remember { mutableStateOf(false) }


    BasicAlertDialog({ viewModel.toggleCreateAccountDialog() }) {
        Surface() {
            Column() {
                Text("Create Account")
                TextField(uiState.usernameTextFieldState, label = { Text("Username") })
                TextField(uiState.passwordTextFieldState, label = { Text("Password") })
                TextField(uiState.emailTextFieldState, label = { Text("Email") })
                TextField(uiState.displayNameTextFieldState, label = { Text("Display Name") })


                // Select Group
                ExposedDropdownMenuBox(
                    expanded = groupDropdownExpanded,
                    onExpandedChange = { groupDropdownExpanded = it }
                ) {
                    TextField(
                        value = uiState.selectedGroup,
                        onValueChange = {},
                        readOnly = true,
                        label = { Text("Group") },
                        trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(expanded = groupDropdownExpanded) },
                        colors = ExposedDropdownMenuDefaults.textFieldColors(),
                        modifier = Modifier.menuAnchor()
                    )
                    ExposedDropdownMenu(
                        expanded = groupDropdownExpanded,
                        onDismissRequest = { groupDropdownExpanded = false }
                    ) {
                        uiState.groups.forEach { group ->
                            DropdownMenuItem(
                                text = { Text(group) },
                                onClick = {
                                    viewModel.selectGroup(group)
                                    groupDropdownExpanded = false
                                }
                            )
                        }
                    }
                }




                Button({
                    viewModel.createAccount()
                }) {
                    Text("Create")
                }
            }
        }
    }

}



class AccountsViewModel: ViewModel() {

    private val _uiState = MutableStateFlow(AccountsUiState())
    val uiState = _uiState.asStateFlow()
    val client = KtorClient.instance.client
    val scope = CoroutineScope(Dispatchers.Default)

    fun loadAccounts() {
        scope.launch {
            val response = client.get("/admin/accounts/list")
            println("Response: $response")

            val users = response.body<List<UserDTO>>()

            println("Users: $users")

            _uiState.value = _uiState.value.copy(users = users)
        }
    }

    fun loadGroups() {
        TODO("Not yet implemented")
    }

    fun toggleCreateAccountDialog() {
        _uiState.value = _uiState.value.copy(showCreateAccountDialog = !_uiState.value.showCreateAccountDialog)
    }

    fun createAccount() {
        scope.launch {

            val group = UserGroups.valueOf(uiState.value.selectedGroup)

            val body = UserDTO(
                username = uiState.value.usernameTextFieldState.text.toString(),
                password = uiState.value.passwordTextFieldState.text.toString(),
                email = uiState.value.emailTextFieldState.text.toString(),
                displayName = uiState.value.displayNameTextFieldState.text.toString(),
                group = group
            )
            val response = client.post("/admin/accounts") {
                contentType(Json)
                setBody(body)
            }

            println("createAccount Response: $response")

            loadAccounts()
            toggleCreateAccountDialog()
        }
    }

    fun selectGroup(group: String) {
        _uiState.update { it.copy(selectedGroup = group) }
    }

}